import { useNavigate, useParams } from 'react-router-dom';
import { useGetPracticeRevisionExercises } from '../../hooks/getRevisionExercises';
import { Fragment, useEffect, useRef, useState } from 'react';
import { practiceRevisionExercisesStore } from '../../stores/practiceRevisionExercisesStore';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { ExerciseAction, IExercise } from '../../../../core/models/exercise';
import { useDeletePracticeRevision } from '../../hooks/deleteRevision';
import { useFinishPracticeRevision } from '../../hooks/finishRevision';
import { useCheckPracticeRevisionQuestion } from '../../hooks/checkAnswer';
import { useFinishWithAttemptRevision } from '../../hooks/finishWithAttemptRevision';
import { useUserStore } from '../../../Auth/stores/authStore';
import Container from '../../../../core/components/atoms/Container';
import ConfirmationModalRevision from '../../components/ConfirmationModalRevision';
import Exercise from '../../../../core/components/templates/Exercise';
import Confetti from 'react-confetti';
import NoContent from '../../../../core/components/templates/NoContent';

export function PracticeRevisionExercise() {
  const { id } = useParams();
  const navigate = useNavigate();
  const divRef = useRef<HTMLDivElement>(null);

  const { mutate, isPending, isError } = useGetPracticeRevisionExercises();
  const {
    mutate: deleteRevision,
    isPending: deletePending,
    isSuccess: deleteSuccess,
    data: deleteData,
  } = useDeletePracticeRevision();
  const {
    mutate: finishRevision,
    isPending: finishPending,
    isSuccess: finishSuccess,
    data: finishData,
  } = useFinishPracticeRevision();
  const {
    mutate: finishWithAttempt,
    isPending: isLoading,
    isSuccess: finishWithAttemptSuccess,
    data: finishWithAttemptData,
  } = useFinishWithAttemptRevision();
  const { mutate: checkAnswer } = useCheckPracticeRevisionQuestion();

  const { revisionExercises } = practiceRevisionExercisesStore();
  const { user } = useUserStore();

  const [exercises, setExercises] = useState<Partial<IExercise>[]>([]);
  const [openOnFinishOne, setOpenOnFinishOne] = useState(false);
  const [openOnFinishTwo, setOpenOnFinishTwo] = useState(false);
  const [openOnFinishThree, setOpenOnFinishThree] = useState(false);

  const [rightAnswer, setRightAnswer] = useState<boolean>(false);

  useEffect(() => {
    if (revisionExercises && revisionExercises.exercises.length > 0) {
      const exercises = revisionExercises.exercises;
      const newArray = exercises.map((it) => {
        if (it.alternatives) {
          const optionsIdx = it.alternatives
            ?.map((exe, i) => (exe.selected === true ? i : -1))
            .filter((elem) => elem >= 0);

          if (optionsIdx) {
            for (let x = 0; x < optionsIdx.length; x++) {
              const idx = optionsIdx[x];
              it.alternatives[idx].status =
                it.status === 'incorrect' ? 'error' : 'correct';
            }
          }

          it.alternatives = it.alternatives.map((alt) => ({
            ...alt,
            id: alt._id,
          }));
        }

        return {
          ...it,
          id: it._id,
          done: it.answered,
          correct: it.status === 'incorrect' ? false : true,
          exerciseType: it.exercise_type,
          selected: false,
        };
      });

      newArray[0].selected = true;
      setExercises(newArray);
    }
  }, [revisionExercises]);

  useEffect(() => {
    if (
      (deleteSuccess && !deleteData.display) ||
      (finishSuccess && finishData.id) ||
      (finishWithAttemptSuccess && finishWithAttemptData.dateToReview)
    ) {
      navigate('/simulados/reviews');
    }
  }, [deletePending, finishPending, isLoading]);

  useEffect(() => {
    if (id) {
      mutate(id);
    }
  }, [id]);

  useEffect(() => {
    if (rightAnswer) {
      setTimeout(function () {
        setRightAnswer(false);
      }, 7000);
    }
  }, [rightAnswer]);

  const handlePageChange = (page: number) => {
    const aux = [...exercises];
    const idx = aux.findIndex((it) => it.selected === true);

    aux[idx].selected = false;
    aux[page].selected = true;
  };

  const handleExerciseChange = (
    exercise: IExercise,
    action?: ExerciseAction
  ) => {
    const aux = [...exercises];
    const idx = aux.findIndex((it) => {
      const idExercise = it.id;
      const idNew = exercise.id;

      return idExercise === idNew;
    });

    aux[idx] = exercise;

    if (action === 'verifyAnswer') {
      const selected = exercise.alternatives.filter((it) => it.selected);
      const answer = selected.map((it) => {
        return it._id || it.id;
      });
      const isAllCorrect = selected.filter((it) => it.correct === false);

      if (isAllCorrect.length === 0) {
        setRightAnswer(true);

        const audio = new Audio(
          'https://storage.aprovatotal.com.br/platform-storage-prod/sounds/songCorrectExercise.mp3'
        );
        if (user && user.exercisePlaySound) {
          audio.play();
        }
      }

      checkAnswer({
        body: {
          answer: [...answer],
          correct: isAllCorrect.length > 0 ? false : true,
          question: exercise._id || exercise.id,
        },
        id: id || '',
      });
    }

    setExercises(aux);
  };

  const handleEndRevision = () => {
    const corrects = exercises.filter((it) => it?.correct);

    if (corrects.length === exercises.length) {
      setOpenOnFinishOne(true);
    }

    if (corrects.length === 0) {
      setOpenOnFinishTwo(true);
    }

    if (corrects.length > 0 && corrects.length < exercises.length) {
      setOpenOnFinishThree(true);
    }
  };

  const handleFinish = (type: string) => {
    if (type === '1' && id) {
      deleteRevision(id);
    } else if (type === '4' && id) {
      finishWithAttempt(id);
    } else if (id) {
      finishRevision(id);
    }
  };

  if (isError) {
    const url = `/simulados/enem`;

    return <NoContent url={url} />;
  }

  return (
    <Fragment>
      {rightAnswer && user?.exerciseShowAnimation && (
        <div className='w-full' ref={divRef}>
          <Confetti
            width={
              divRef.current?.offsetWidth ? divRef.current?.offsetWidth : 0
            }
            height={divRef.current?.offsetParent?.clientHeight}
            numberOfPieces={300}
            onConfettiComplete={() => setRightAnswer(false)}
          />
        </div>
      )}
      <Container
        gap='xxl'
        direction='col'
        className='xs:mb-[30px] xl:mb-[30px]'
      >
        {isPending && <p> Carregando... </p>}
        <Breadcrumbs
          items={[
            {
              label: 'Revisões',
              href: '/simulados/reviews',
            },
            {
              label: revisionExercises?.subject || 'Assunto',
              href: '/simulados/revisao/' + revisionExercises?._id,
            },
          ]}
        />
        <Exercise
          title={revisionExercises?.subject}
          discipline={revisionExercises?.area_title}
          exercises={(exercises as IExercise[]) || []}
          exerciseType='revision'
          showExerciseNavigation
          isLoading={isPending}
          onPageChange={(page) => handlePageChange(page)}
          onExerciseChange={(exercise: IExercise, action?: ExerciseAction) =>
            handleExerciseChange(exercise, action)
          }
          onEndRevision={() => handleEndRevision()}
        />
        <ConfirmationModalRevision
          isOpen={openOnFinishOne}
          buttonOkText='Encerrar revisão'
          buttonCancelText='Continuar revisando em 2 dias'
          onClose={() => handleFinish('4')}
          onConfirm={() => handleFinish('1')}
          text={`
            Parabéns! Você acertou 2 de 2 questões. \n
            Você quer continuar revisando questões sobre esse assunto? \n
            Se quiser continuar a revisão, você poderá revisar o assunto 
            ${revisionExercises?.subject} em 2 dias.`}
        />
        <ConfirmationModalRevision
          isOpen={openOnFinishTwo}
          buttonOkText='Revisão disponível amanhã'
          onClose={() => setOpenOnFinishTwo(false)}
          onConfirm={() => handleFinish('2')}
          text={`
            Você não acertou nenhuma questão sobre o assunto ${revisionExercises?.subject}. \n
            Você ainda precisa revisar esse assunto um pouco mais.
          `}
        />
        <ConfirmationModalRevision
          isOpen={openOnFinishThree}
          buttonOkText='Revisar em 2(dois) dias'
          onClose={() => setOpenOnFinishThree(false)}
          onConfirm={() => handleFinish('3')}
          text={`
            Você acertou ${exercises.filter((it) => it.correct).length} de 
            ${revisionExercises?.exercises.length} questões sobre o 
            assunto ${revisionExercises?.subject}. \n
            Você ainda precisa revisar esse assunto um pouco mais.
          `}
        />
      </Container>
    </Fragment>
  );
}
